import { z } from 'zod';
/**
 * Session success (Warren, 28 Oct 2026, predictive maintenance addendum item
 * 2): "give every failed start and every short session a cause (wrong
 * connector, timeout, refused by the start guard, fee or tariff mismatch,
 * payment, charger stop with reason Other, our own crash or roll), shown on
 * the session and rolled up per charger and per site. Show start success rate
 * on the charger page and the anomalies page. Map each cause to its fix ...
 * Target 98% successful starts per site; below that is an anomaly."
 *
 * One vocabulary for the Api (the classifier sweep in apps/api
 * lib/sessionOutcomes, the rollup and its route) and Admin (the "What
 * happened" card, the charger page card): each cause carries its plain-word
 * label, one sentence, who owns it, how it counts and the fix it maps to.
 *
 * How a cause counts (`tally`):
 *   success   a start that started, or a session that ended normally
 *   failure   counted against the 98% target
 *   excluded  shown, never in the rate: the driver's side (a card decline,
 *             no payment method, a cancel inside the plug-in window) and
 *             access rules working as the operator set them (Warren's
 *             ruling 1, 29 Oct 2026)
 *
 * Owners: our platform (evi, including our own roll or crash, ruling 2), the
 * site (charger offline, the charger went dark), the charger hardware, the
 * configuration (fees, contracts, billing), the driver or vehicle, unknown (a
 * start accepted with no car plugged in and no evidence where the car was,
 * ruling 5), or none (a success).
 */
export const START_SUCCESS_TARGET = 0.98;
/** Below this, or 0 started of START_SUCCESS_CRITICAL_ZERO_OF or more, is critical (ruling 3). */
export const START_SUCCESS_CRITICAL_RATE = 0.8;
export const START_SUCCESS_CRITICAL_ZERO_OF = 3;
/** Ruling 3: 2 or more counted failures in 7 days, or 10 or more counted attempts in 30 days, below the target. */
export const START_SUCCESS_MIN_FAILED_7D = 2;
export const START_SUCCESS_MIN_COUNTED_30D = 10;
/** Ruling 4: a charger cut in the first 5 minutes counts against session completion, not start success. */
export const EARLY_CUT_MINUTES = 5;
export const OUTCOME_OWNERS = ['evi', 'site', 'charger', 'config', 'driver', 'unknown', 'none'];
export const OUTCOME_TALLIES = ['success', 'failure', 'excluded'];
export const OUTCOME_FIXES = [
    'redirect_connector',
    'redirect_connector_late',
    'redirect_sibling',
    'pending_cancel_countdown',
    'block_config_at_save',
    'assign_tariff',
    'billing_setup',
    'ladder_a',
    'ladder_b',
    'ladder_d',
    'platform',
    'connection_timeout',
    'driver_hint',
];
export const OUTCOME_FIX_INFO = {
    redirect_connector: { label: 'The wrong-connector redirect: start the connector the car is in', status: 'live' },
    // The redirect decides at the tap; a car that reaches the other connector after it needs the start re-issued.
    redirect_connector_late: { label: 'Driver scans and confirms the charger and connector before retrying', status: 'live' },
    redirect_sibling: { label: 'Driver scans and confirms the charger and connector before retrying', status: 'live' },
    pending_cancel_countdown: { label: 'The pending-cancel countdown', status: 'not_built' },
    block_config_at_save: { label: 'Block the fee or contract change at save', status: 'live' },
    assign_tariff: { label: 'Assign an active tariff to the charger', status: 'manual' },
    billing_setup: { label: 'Finish the operator or fleet billing setup (EVI ops)', status: 'manual' },
    ladder_a: { label: 'Remediation step a: ask the charger for its status', status: 'ladder' },
    ladder_b: { label: 'Remediation step b: cycle the connector, then a soft reset', status: 'ladder' },
    ladder_d: { label: 'Remediation step d: ask the building to cycle the breaker', status: 'ladder' },
    platform: { label: 'EVI: roll discipline and error tracking', status: 'manual' },
    connection_timeout: { label: "Check the charger's plug-in window (ConnectionTimeOut)", status: 'manual' },
    driver_hint: { label: 'The app tells the driver what to do', status: 'live' },
};
export const START_CAUSES = [
    'started',
    'platform_roll',
    'platform_unreachable',
    'start_guard_fee_mismatch',
    'start_guard_invalid_contract',
    'billing_not_configured',
    'payment_setup_failed',
    'charger_offline',
    'charger_no_answer',
    'charger_holding_prior_start',
    'charger_faulted',
    'charger_rejected',
    'wrong_connector',
    'wrong_pedestal',
    'timeout_no_plug',
    'api_interrupted',
    'server_error',
    'rfid_refused_unexplained',
    'unclassified',
    'payment_declined',
    'driver_payment',
    'driver_cancelled',
    'access_rule',
    'driver_input',
    'card_blocked',
    'unknown_tag',
    'inactive_card',
    'concurrent_tx',
    'maintenance_window',
    'connector_switched_off',
    'admin_start',
];
export const SESSION_CAUSES = [
    'platform_roll',
    'charger_went_dark',
    'stopped_by_operator',
    'charger_fault',
    'charger_cut',
    'charger_aborted',
    'charger_no_power',
    'vehicle_did_not_draw',
    'charger_stop_idle',
    'emergency_stop',
    'deauthorized',
    'unplugged_while_charging',
    'vehicle_full',
    'short_driver_stop',
    'ok',
    'session_unclassified',
];
export const OUTCOME_CAUSE_INFO = {
    started: { label: 'Started', sentence: 'The charger started the session.', owner: 'none', tally: 'success', fix: null },
    platform_roll: {
        label: 'Our platform restarted or crashed',
        sentence: 'This failed while our charger service was restarting or had crashed. EVI owns it, not the site.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    platform_unreachable: {
        label: 'Our charger service was unreachable',
        sentence: 'The Api could not reach our charger service to send the start.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    start_guard_fee_mismatch: {
        label: 'Fee differs from the site contract',
        sentence: "The charger's connection fee differs from the approved site contract, so the start guard refused the start.",
        owner: 'config', tally: 'failure', fix: 'block_config_at_save',
    },
    start_guard_invalid_contract: {
        label: 'Site contract needs review',
        sentence: 'The site contract or its binding to this charger failed the start guard check.',
        owner: 'config', tally: 'failure', fix: 'block_config_at_save',
    },
    billing_not_configured: {
        label: 'Billing not set up',
        sentence: 'The operator or fleet billing is not set up, so the start was refused.',
        owner: 'config', tally: 'failure', fix: 'billing_setup',
    },
    payment_setup_failed: {
        label: 'Payment could not be set up',
        sentence: "The card hold failed on our side, not because of the driver's card.",
        owner: 'evi', tally: 'failure', fix: 'billing_setup',
    },
    charger_offline: {
        label: 'Charger offline',
        sentence: 'The charger was not connected when the driver tapped Start.',
        owner: 'site', tally: 'failure', fix: 'ladder_d',
    },
    charger_no_answer: {
        label: 'Charger did not answer',
        sentence: 'The charger did not answer the start command within 30 seconds.',
        owner: 'charger', tally: 'failure', fix: 'ladder_a',
    },
    charger_holding_prior_start: {
        label: 'Charger still held an earlier start',
        sentence: 'The charger refused because an earlier start on the same connector was still waiting for a car.',
        owner: 'evi', tally: 'failure', fix: 'pending_cancel_countdown',
    },
    charger_faulted: {
        label: 'Connector faulted',
        sentence: 'The charger refused the start while the connector reported a fault or was unavailable.',
        owner: 'charger', tally: 'failure', fix: 'ladder_b',
    },
    charger_rejected: {
        label: 'Charger refused the start',
        sentence: 'The charger answered Rejected to the start command.',
        owner: 'charger', tally: 'failure', fix: 'ladder_a',
    },
    wrong_connector: {
        label: 'Wrong connector',
        sentence: 'The car was plugged into another connector on this charger, and the start went to the empty one.',
        owner: 'evi', tally: 'failure', fix: 'redirect_connector',
    },
    wrong_pedestal: {
        label: 'Car at another charger',
        sentence: 'A cable went into another charger at this site while this one waited for the car.',
        owner: 'evi', tally: 'failure', fix: 'redirect_sibling',
    },
    timeout_no_plug: {
        label: 'No car plugged in',
        sentence: 'The charger accepted the start, but no car was plugged in before its plug-in window closed.',
        owner: 'unknown', tally: 'failure', fix: 'connection_timeout',
    },
    api_interrupted: {
        label: 'Our Api stopped mid-start',
        sentence: 'The start was interrupted on our side before the command reached the charger.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    server_error: {
        label: 'Server error',
        sentence: 'Our server failed while starting the session.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    rfid_refused_unexplained: {
        label: 'Card start refused',
        sentence: 'We refused a card start at the charger for a reason the stored records do not show.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    unclassified: {
        label: 'Not explained yet',
        sentence: 'This start matched no known pattern. EVI looks at every one.',
        owner: 'evi', tally: 'failure', fix: 'platform',
    },
    payment_declined: {
        label: 'Card declined',
        sentence: "The driver's card was declined.",
        owner: 'driver', tally: 'excluded', fix: 'driver_hint',
    },
    driver_payment: {
        label: 'Driver payment needed',
        sentence: 'The driver had to add or fix a payment method, top up the wallet, or pay a balance first.',
        owner: 'driver', tally: 'excluded', fix: 'driver_hint',
    },
    driver_cancelled: {
        label: 'Driver cancelled',
        sentence: 'The driver cancelled inside the plug-in window.',
        owner: 'driver', tally: 'excluded', fix: null,
    },
    access_rule: {
        label: 'Access rule',
        sentence: 'An access rule set for this charger refused the start, working as set.',
        owner: 'site', tally: 'excluded', fix: null,
    },
    driver_input: {
        label: 'Driver input',
        sentence: 'The request did not fit this charger, or the driver had to plug in first.',
        owner: 'driver', tally: 'excluded', fix: 'driver_hint',
    },
    card_blocked: {
        label: 'Card blocked',
        sentence: 'The card was blocked, for example by an unpaid balance or a private charger.',
        owner: 'driver', tally: 'excluded', fix: 'driver_hint',
    },
    unknown_tag: {
        label: 'Unknown card',
        sentence: 'A card that is not registered with us was tapped.',
        owner: 'driver', tally: 'excluded', fix: null,
    },
    inactive_card: {
        label: 'Switched-off card',
        sentence: 'A card that its owner or an admin switched off was tapped.',
        owner: 'driver', tally: 'excluded', fix: null,
    },
    concurrent_tx: {
        label: 'Card already charging',
        sentence: 'The card was already in use on another session.',
        owner: 'driver', tally: 'excluded', fix: null,
    },
    maintenance_window: {
        label: 'Maintenance window',
        sentence: 'The charger was inside a scheduled maintenance window.',
        owner: 'site', tally: 'excluded', fix: null,
    },
    connector_switched_off: {
        label: 'Connector switched off',
        sentence: 'The connector was out of service on purpose (switched off from Admin, a script or the charger\'s own portal), so the charger refused the start.',
        owner: 'site', tally: 'excluded', fix: null,
    },
    admin_start: {
        label: 'Started by staff',
        sentence: 'A remote start sent by staff, not a driver. Not part of the rate.',
        owner: 'none', tally: 'excluded', fix: null,
    },
    charger_went_dark: {
        label: 'Charger lost contact',
        sentence: 'The charger stopped talking to us during the session, and the session was closed for it.',
        owner: 'site', tally: 'failure', fix: 'ladder_d',
    },
    stopped_by_operator: {
        label: 'Stopped remotely',
        sentence: 'EVI or the site stopped the session remotely (a remote stop, a reset or an unlock).',
        owner: 'site', tally: 'excluded', fix: null,
    },
    charger_fault: {
        label: 'Charger fault',
        sentence: 'The charger ended the session and reported a fault on the connector.',
        owner: 'charger', tally: 'failure', fix: 'ladder_b',
    },
    charger_cut: {
        label: 'Charger cut the session',
        sentence: 'The charger ended the session while it was still charging (stop reason Other, PowerLoss or Reboot).',
        owner: 'charger', tally: 'failure', fix: 'ladder_b',
    },
    charger_aborted: {
        label: 'Charger ended it before charging',
        sentence: 'The charger ended the session before any real energy flowed (stop reason Other, PowerLoss or Reboot).',
        owner: 'charger', tally: 'failure', fix: 'ladder_b',
    },
    charger_no_power: {
        label: 'Charger gave no power',
        sentence: 'The charger never delivered energy: it held the car in SuspendedEVSE.',
        owner: 'charger', tally: 'failure', fix: 'ladder_b',
    },
    vehicle_did_not_draw: {
        label: 'Vehicle did not draw power',
        sentence: 'The charger was ready, but the vehicle never drew power (SuspendedEV).',
        owner: 'driver', tally: 'excluded', fix: null,
    },
    charger_stop_idle: {
        label: 'Charger ended an idle session',
        sentence: 'The charger ended the session after the car had stopped drawing power.',
        owner: 'charger', tally: 'success', fix: null,
    },
    emergency_stop: {
        label: 'Emergency stop',
        sentence: "Someone pressed the charger's emergency stop.",
        owner: 'site', tally: 'success', fix: null,
    },
    deauthorized: {
        label: 'Card deauthorized',
        sentence: 'The charger ended the session because the card was no longer authorized.',
        owner: 'unknown', tally: 'success', fix: null,
    },
    unplugged_while_charging: {
        label: 'Unplugged while charging',
        sentence: 'The cable was unplugged while the car was still charging.',
        owner: 'driver', tally: 'success', fix: null,
    },
    vehicle_full: {
        label: 'Car finished charging',
        sentence: 'The car stopped drawing power and the session ended normally.',
        owner: 'driver', tally: 'success', fix: null,
    },
    short_driver_stop: {
        label: 'Short session, ended by the driver',
        sentence: 'The driver or the vehicle ended a short session.',
        owner: 'driver', tally: 'success', fix: null,
    },
    ok: { label: 'Completed normally', sentence: 'The session ended normally.', owner: 'none', tally: 'success', fix: null },
    session_unclassified: {
        label: 'No stop record',
        sentence: 'No stop frame was found for this session, so its end cannot be explained yet.',
        owner: 'evi', tally: 'excluded', fix: 'platform',
    },
};
/** Failures from our own roll or crash: counted, owned by EVI, and folded into one network row instead of raising every site (ruling 2). */
export const NETWORK_FOLD_CAUSES = new Set(['platform_roll', 'platform_unreachable', 'api_interrupted']);
/** Session flags shown beside the cause on the card (staff only). */
export const OUTCOME_FLAGS = ['tariff_fallback', 'payment_unpaid', 'payment_review'];
export const OUTCOME_FLAG_LABELS = {
    tariff_fallback: 'Billed at the fallback price: the session names a tariff that does not exist',
    payment_unpaid: 'A card balance for this session is still unpaid',
    payment_review: 'The payment needs review',
};
/** The cause a Site persona reads in place of a driver-side one (ruling 7). */
export const SITE_DRIVER_SIDE_CAUSE = 'driver_or_vehicle';
export const SITE_DRIVER_SIDE_LABEL = 'Ended by the driver or vehicle';
export function isOutcomeCause(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(OUTCOME_CAUSE_INFO, value);
}
export function outcomeCauseInfo(cause) {
    return isOutcomeCause(cause) ? OUTCOME_CAUSE_INFO[cause] : null;
}
/** Plain words for a cause key, the Site's collapsed key included. */
export function outcomeCauseLabel(cause) {
    if (cause === SITE_DRIVER_SIDE_CAUSE)
        return SITE_DRIVER_SIDE_LABEL;
    return outcomeCauseInfo(cause)?.label ?? cause.replace(/_/g, ' ');
}
/** Who the fix waits on, in plain words; null when nothing is owed. */
export function outcomeWaitingOn(owner, fix) {
    if (fix && OUTCOME_FIX_INFO[fix].status === 'ladder')
        return 'The automatic fixes, then the building';
    switch (owner) {
        case 'evi': return 'EVI';
        case 'config': return 'EVI ops (configuration)';
        case 'site': return 'The site';
        case 'charger': return 'The charger';
        case 'unknown': return 'Unknown';
        default: return null;
    }
}
/** A cause key as a Site persona may read it: driver-side causes collapse (ruling 7). */
export function siteCauseKey(cause) {
    return outcomeCauseInfo(cause)?.owner === 'driver' ? SITE_DRIVER_SIDE_CAUSE : cause;
}
/** A cause tally map with the driver-side causes folded for a Site persona. */
export function siteCauseCounts(causes) {
    const out = {};
    for (const [cause, n] of Object.entries(causes)) {
        const key = siteCauseKey(cause);
        out[key] = (out[key] ?? 0) + n;
    }
    return out;
}
// ── The stored outcome, as Admin reads it ────────────────────────────────
export const SessionOutcomeViewSchema = z.object({
    kind: z.enum(['start', 'session']),
    cause: z.string(),
    tally: z.enum(OUTCOME_TALLIES),
    counted: z.boolean(),
    owner: z.enum(OUTCOME_OWNERS),
    fix: z.enum(OUTCOME_FIXES).nullable(),
    /** 'redirected': the wrong-connector redirect started the connector the car was in. 'early_cut': a cut inside EARLY_CUT_MINUTES. */
    note: z.string().nullable(),
    stop_reason: z.string().nullable(),
    late_stop_reason: z.string().nullable().optional(),
    flags: z.array(z.enum(OUTCOME_FLAGS)),
    evidence: z.record(z.string(), z.unknown()),
    rule_version: z.number().int(),
    classified_at: z.string(),
});
/** What a Site persona reads of a session's outcome: the cause and its owner, driver-side causes collapsed, no flags or evidence. */
export const SessionOutcomeSummarySchema = z.object({
    kind: z.enum(['start', 'session']),
    cause: z.string(),
    label: z.string(),
    owner: z.enum(OUTCOME_OWNERS),
    counted: z.boolean(),
    note: z.string().nullable(),
});
export function siteOutcomeSummary(outcome) {
    if (!outcome || typeof outcome.cause !== 'string')
        return null;
    const key = siteCauseKey(outcome.cause);
    const collapsed = key === SITE_DRIVER_SIDE_CAUSE;
    const owner = OUTCOME_OWNERS.includes(String(outcome.owner)) ? outcome.owner : 'unknown';
    return {
        kind: outcome.kind === 'start' ? 'start' : 'session',
        cause: key,
        label: outcomeCauseLabel(key),
        owner: collapsed ? 'driver' : owner,
        counted: outcome.counted === true,
        note: collapsed ? null : outcome.note ?? null,
    };
}
// ── GET /api/dashboard/start-success ─────────────────────────────────────
export const START_SUCCESS_RANGES = ['7d', '30d'];
const rate = z.number().min(0).max(1).nullable();
export const StartSuccessWindowSchema = z.object({
    counted: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    rate,
});
export const StartSuccessRowSchema = z.object({
    /** Every start attempt in the window: counted, excluded and pending. */
    attempts: z.number().int().nonnegative(),
    /** succeeded + failed: the rate's denominator. */
    counted: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    rate,
    /** Promoted sessions that ended normally over every classified promoted session (ruling 4), null with none. */
    completion_rate: rate,
    sessions: z.number().int().nonnegative(),
    sessions_failed: z.number().int().nonnegative(),
    /** Every non-success cause in the window, failures and excluded alike. */
    causes: z.record(z.string(), z.number().int().nonnegative()),
    /** Ruling 3, over the 7 and 30 days ending now, whatever the window: an anomaly. */
    below_target: z.boolean(),
    critical: z.boolean(),
    completion_below_target: z.boolean(),
    /** Every counted start failure behind a start breach is our own roll or crash: no site row, the network row carries it (ruling 2). */
    network_only: z.boolean(),
    /** The same for the completion rate: every counted session failure behind its breach is our own roll or crash. */
    completion_network_only: z.boolean(),
    last_failure_at: z.string().nullable(),
    window_7d: StartSuccessWindowSchema,
    window_30d: StartSuccessWindowSchema,
    /** The same two windows with our own roll's and crash's failures left out: the rates below_target is judged on, which a
     *  site row's words name (round 5). */
    own_7d: StartSuccessWindowSchema.optional(),
    own_30d: StartSuccessWindowSchema.optional(),
});
export const StartSuccessNetworkSchema = z.object({
    /** Starts and sessions our own roll or crash failed, together (sessions_failed of them sessions). */
    failed: z.number().int().nonnegative(),
    sessions_failed: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
    chargers: z.number().int().nonnegative(),
    causes: z.record(z.string(), z.number().int().nonnegative()),
    first_at: z.string().nullable(),
    last_at: z.string().nullable(),
});
export const StartSuccessChargerRowSchema = StartSuccessRowSchema.extend({
    charger_id: z.string(),
    charger_identity: z.string(),
    display_name: z.string().nullable(),
    location_id: z.string().nullable(),
    location_name: z.string().nullable(),
});
export const StartSuccessLocationRowSchema = StartSuccessRowSchema.extend({
    location_id: z.string(),
    location_name: z.string(),
    charger_count: z.number().int().nonnegative(),
});
export const StartSuccessResponseSchema = z.object({
    chargers: z.array(StartSuccessChargerRowSchema),
    locations: z.array(StartSuccessLocationRowSchema),
    network: StartSuccessNetworkSchema,
    range: z.enum(START_SUCCESS_RANGES),
    since: z.string(),
    /** Start attempts are counted from the deploy of the classifier (ruling 6); before it, the backfill's floor. */
    counting_from: z.string().nullable(),
    target: z.number(),
    /** True for a Site persona: driver-side causes read as one. */
    causes_collapsed: z.boolean(),
    generated_at: z.string(),
});
export const StartAttemptViewSchema = z.object({
    id: z.string(),
    at: z.string(),
    source: z.enum(['app', 'rfid', 'admin']),
    connector_id: z.number().int().nullable(),
    requested_connector_id: z.number().int().nullable(),
    result: z.enum(['pending', 'started', 'failed', 'excluded']),
    cause: z.string().nullable(),
    label: z.string().nullable(),
    owner: z.enum(OUTCOME_OWNERS).nullable(),
    fix: z.enum(OUTCOME_FIXES).nullable(),
    note: z.string().nullable(),
    session_id: z.string().nullable(),
});
export const StartAttemptsResponseSchema = z.object({
    charger_id: z.string(),
    range: z.enum(START_SUCCESS_RANGES),
    attempts: z.array(StartAttemptViewSchema),
    causes_collapsed: z.boolean(),
    generated_at: z.string(),
});
//# sourceMappingURL=session-outcome.js.map